import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";

const DIRECTOR_ROLES = ["tech_director", "club_rep", "admin"];
const SCOUT_ROLES = ["head_scout", "scout"];
const COACH_ROLES = ["head_coach", "asst_coach"];
const MEDICAL_ROLES = ["club_doctor", "physio"];
const STAFF_ROLES = [...COACH_ROLES, ...MEDICAL_ROLES];
const CLUB_SIDE_ROLES = [...DIRECTOR_ROLES, ...SCOUT_ROLES, ...STAFF_ROLES];

// Agent-only surfaces — these are blocked for all club-side roles.
const AGENT_ONLY = [
  "/dashboard", "/clients", "/contracts", "/payroll",
  "/legal", "/travel-planner", "/compliance",
];

// Club-only surfaces — Position map renders an agent-roster variant for
// agents/admins but the formation-window features remain club-side only.
// Keep /club-staff club-only; Position map is now available to everyone.
const CLUB_ONLY = ["/club-staff"];

// Admin-only: audit log surfaces every workspace event. Non-admins get
// redirected to the dashboard rather than seeing an empty page with
// admin-only badges.
const ADMIN_ONLY = ["/audit-log", "/admin"];

// Scouting/Staff specific homes
const SCOUT_HOME = "/scouting-home";
const STAFF_HOME = "/staff-home";
const TD_HOME = "/td-home";

const CLIENT_BLOCKED = [
  "/clients", "/contracts", "/payroll", "/legal",
  "/travel-planner", "/compliance", "/position-map",
];

function homeFor(role) {
  if (role === "tech_director") return TD_HOME;
  if (SCOUT_ROLES.includes(role)) return SCOUT_HOME;
  if (STAFF_ROLES.includes(role)) return STAFF_HOME;
  if (role === "client") return "/dashboard";
  return "/dashboard";
}

function isBlocked(path, prefixes) {
  return prefixes.some((p) => path === p || path.startsWith(p + "/"));
}

export default function ProtectedRoute({ children }) {
  const { user, authChecked } = useAuth();
  const loc = useLocation();
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500">
        <div className="font-display text-lg tracking-tight">Loading Playmaker…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const role = user.role;
  const path = loc.pathname;
  // Player / guardian accounts live exclusively in the read-only portal
  if (role === "player" || role === "guardian") return <Navigate to="/portal" replace />;

  // Agent-only block for all club-side roles
  if ([...DIRECTOR_ROLES.filter((r) => r === "tech_director"), ...SCOUT_ROLES, ...STAFF_ROLES].includes(role) && isBlocked(path, AGENT_ONLY)) {
    return <Navigate to={homeFor(role)} replace />;
  }
  // Club-only block for agent / associate roles (Position map is club-side)
  if (!CLUB_SIDE_ROLES.includes(role) && role !== "client" && isBlocked(path, CLUB_ONLY)) {
    return <Navigate to="/dashboard" replace />;
  }
  // Client block
  if (role === "client" && isBlocked(path, CLIENT_BLOCKED)) {
    return <Navigate to="/dashboard" replace />;
  }
  // Admin-only block
  if (role !== "admin" && isBlocked(path, ADMIN_ONLY)) {
    return <Navigate to="/dashboard" replace />;
  }
  // /td-home, /scouting-docs reserved for director-side
  if ([TD_HOME].some((p) => path === p || path.startsWith(p + "/"))) {
    if (!DIRECTOR_ROLES.includes(role)) return <Navigate to={homeFor(role)} replace />;
  }
  // /scouting-home only for scouts (+ TD/admin can peek)
  if (path === SCOUT_HOME || path.startsWith(SCOUT_HOME + "/")) {
    if (!SCOUT_ROLES.includes(role) && !DIRECTOR_ROLES.includes(role)) {
      return <Navigate to={homeFor(role)} replace />;
    }
  }
  // /staff-home only for staff (+ TD/admin can peek)
  if (path === STAFF_HOME || path.startsWith(STAFF_HOME + "/")) {
    if (!STAFF_ROLES.includes(role) && !DIRECTOR_ROLES.includes(role)) {
      return <Navigate to={homeFor(role)} replace />;
    }
  }
  // /scouting-docs available to TD + scout + staff
  if (path === "/scouting-docs" || path.startsWith("/scouting-docs/")) {
    if (![...DIRECTOR_ROLES, ...SCOUT_ROLES, ...STAFF_ROLES].includes(role)) {
      return <Navigate to={homeFor(role)} replace />;
    }
  }
  return children;
}
